import { useMemo, useContext } from 'react';

import _ from 'lodash';

import { useCurrentModelRunData } from './useModelRuns';
import { FeatureAliasesContext } from '../../../context/MMMFeatureAliases';
import { buildChannelTactics } from '../helpers';

/**
 * useModelTrainingData
 *
 * This hook processes raw data (such as media values and revenue by date) and computes structured training data
 * along with statistics like the minimum, maximum, and average spend for each media type over the last 30 days.
 * The processed data and calculated statistics are returned as outputs.
 *
 * @param {Array} data -  An array of raw input data objects (training_data from MMM api), where each object includes:
 *    - `DATE`: The date of the entry (in `YYYY-MM-DD` format).
 *    - `media`: The name of the media or channel.
 *    - `media_value`: The value or spend amount for that media on the given date.
 *    - `revenue`: The revenue generated on that date for the corresponding media.
 *
 * @returns {Object} An object containing:
 *  - `trainingDates`: An array of dates, extracted from the input data.
 *  - `trainingData`: An array of processed training data objects, where each object contains the media spend information for that date.
 *  - `trainingStats`: An array of media statistics, including:
 *      - `feature`: The media name (e.g., `SEARCH_BING_S`, `EMAIL_SENDS_IMP`).
 *      - `spend_30d`: The total media spend over the last 30 days.
 *      - `spend_mean`: The average daily media spend over the last 30 days.
 *      - `spend_min`: The minimum daily spend recorded for the media.
 *      - `spend_max`: The maximum daily spend recorded for the media.
 *
 * Example:
 * Input:
 * [
 *   { DATE: '2022-01-06', media: 'EMAIL_SENDS_IMP', media_value: 24420, revenue: 24420 },
 *   { DATE: '2022-01-06', media: 'SEARCH_BING_S', media_value: 209.41, revenue: 209.41 },
 *   { DATE: '2022-01-06', media: 'SEARCH_GOOGLE_NONBRAND_S', media_value: 777.27, revenue: 777.27 }
 * ]
 *
 * Output:
 * {
 *   trainingDates: ['2022-01-06'],
 *   trainingData: [
 *     { date: '2022-01-06', EMAIL_SENDS_IMP: 24420, SEARCH_BING_S: 209.41, SEARCH_GOOGLE_NONBRAND_S: 777.27 }
 *   ],
 *   trainingStats: [
 *     { feature: 'EMAIL_SENDS_IMP', spend_30d: 24420, spend_mean: 814, spend_min: 24420, spend_max: 24420 },
 *     { feature: 'SEARCH_BING_S', spend_30d: 209.41, spend_mean: 6.98, spend_min: 209.41, spend_max: 209.41 },
 *     { feature: 'SEARCH_GOOGLE_NONBRAND_S', spend_30d: 777.27, spend_mean: 25.91, spend_min: 777.27, spend_max: 777.27 }
 *   ]
 * }
 */
export const useModelTrainingData = data => {
  const trainingDataObj = useMemo(() => {
    if (!data) return false;
    return data.reduce((accu, cur) => {
      const { DATE, media, media_value } = cur;
      if (!accu[DATE]) accu[DATE] = { date: DATE };
      accu[DATE][media] = media_value;
      return accu;
    }, {});
  }, [data]);

  const trainingStats = useMemo(() => {
    if (!trainingDataObj) return {};
    const trainingData = Object.values(trainingDataObj);

    const trainingMinMax = trainingData.reduce((accu, row) => {
      Object.keys(row).forEach(media => {
        if (media === 'date' || !row[media]) return;
        if (!accu[media]) accu[media] = { min: Infinity, max: 0 };
        if (row[media] < accu[media].min) accu[media].min = row[media];
        if (row[media] > accu[media].max) accu[media].max = row[media];
      });
      return accu;
    }, {});

    const trainingTotals = trainingData.slice(-30).reduce((accu, row) => {
      Object.keys(row).forEach(media => {
        if (media === 'date') return;
        if (!accu[media]) accu[media] = 0;
        accu[media] += row[media];
      });
      return accu;
    }, {});

    return Object.keys(trainingTotals).map(media => {
      const spend = trainingTotals[media];
      const num_spend_days = trainingData.filter(x => x[media] > 0).length;
      const mean = !!num_spend_days ? spend / 30 : 0;
      const { min, max } = trainingMinMax[media] || {};
      return {
        feature: media,
        spend_30d: spend || 0,
        spend_mean: mean || 0,
        spend_min: min || 0,
        spend_max: max || 0,
      };
    });
  }, [data]);

  return {
    trainingDates: Object.keys(trainingDataObj),
    trainingData: Object.values(trainingDataObj),
    trainingStats,
  };
};

/**
 * useModelSeasonality
 *
 * This hook computes seasonality data from a list of holidays from the API. It processes the provided `holidays` array
 * and returns an array of seasonality objects, where each object contains the date and a sum of the values
 * for that date.
 *
 * @param {Array} holidays - An array of objects where each object represents a holiday (seasonality_holidays from MMM api).
 *        Each object contains a `DATE` field (string format) and other fields representing holiday-specific values.
 *
 * @returns {Object} An object containing:
 * - `seasonality`: An array of seasonality objects. Each object includes:
 *   - `date`: The holiday date.
 *   - `sum`: The sum of all non-date fields for that date.
 *   - Additional key-value pairs for each non-date field in the original holiday object.
 *
 * If no holidays are provided, it returns `false`.
 *
 * Example:
 * Input:
 * [
 *   { DATE: '2024-12-25', holiday: 0, monthly: 0, season: 0, trend: 800, weekday: 0 },
 *   { DATE: '2024-12-31', holiday: 5, monthly: 5, season: 5, trend: 50, weekday: 0 },
 * ]
 *
 * Output:
 * [
 *   { date: '2024-12-25', holiday: 0, monthly: 0, season: 0, trend: 800, weekday: 0, sum: 800 },
 *   { date: '2024-12-31', holiday: 0, monthly: 0, season: 0, trend: 800, weekday: 0, sum: 65 }
 * ]
 */
const useModelSeasonality = holidays => {
  const seasonality = useMemo(() => {
    if (!holidays) return false;
    return holidays.map(x => {
      const day = {
        date: x.DATE,
        sum: 0,
      };
      Object.entries(x).forEach(([key, value]) => {
        if (key === 'DATE') return;
        day[key] = value;
        day.sum += value;
      });
      return day;
    });
  }, [holidays]);

  return {
    seasonality,
  };
};

/**
 * useTrainingAndTestDates
 *
 * This hook extracts the `predicted_vs_actual_training` and `predicted_vs_actual_test` data from the input `data` object
 * and computes the start and end dates for both training and test datasets.
 *
 * @param {Object} data - The input data containing `predicted_vs_actual_training` and `predicted_vs_actual_test` arrays.
 *
 * @returns {Object} An object containing the `trainingDateRange` and `testDateRange`.
 * @returns {Array<string>} trainingDateRange - An array containing the start and end dates of the training data.
 * @returns {Array<string>} testDateRange - An array containing the start and end dates of the test data.
 */
export const useTrainingAndTestDates = data => {
  const { predicted_vs_actual_test: predVsActualTestData, predicted_vs_actual_training: predVsActual, training_data: trainingData } = data;

  const trainingDateRange = useMemo(() => {
    if (!predVsActual || !predVsActual.length) return [trainingData[0].DATE, trainingData.slice(-1)[0].DATE];
    return [predVsActual[0].date, predVsActual.slice(-1)[0].date];
  }, [predVsActual, trainingData]);

  const testDateRange = useMemo(() => {
    if (!predVsActual || !predVsActual.length) return [trainingData.slice(-30)[0].DATE, trainingData.slice(-1)[0].DATE];
    return [predVsActualTestData[0].date, predVsActualTestData.slice(-1)[0].date];
  }, [predVsActualTestData, trainingData]);

  const trainingDataDateRange = useMemo(() => [trainingData[0].DATE, trainingData.slice(-1)[0].DATE], [trainingData]);

  return {
    trainingDateRange,
    testDateRange,
    trainingDataDateRange,
  };
};

/**
 * useModelData
 *
 * This hook processes model data for a given execution date (`execDt`) and a set of model runs. It fetches and organizes
 * the training data, training statistics, and seasonality data into a structured format for easy consumption.
 *
 * @param {string} execDt - The execution date to fetch data for, in `YYYY-MM-DD` format.
 * @param {Array} modelRuns - An array of model runs, where each run includes data such as execution dates and other metadata.
 *
 * @returns {Object} An object containing:
 *  - `seasonality`: The processed seasonality data for the specified execution date, if available.
 *  - `rawData`: The raw model data returned for the specified execution date, including any raw features and curves.
 *  - `trainingInfo`: An object containing the processed training information, including:
 *      - `trainingDates`: An array of dates from the `training_data` in the model.
 *      - `trainingData`: The processed training data, where each object represents media spend and performance data for each date.
 *      - `trainingStats`: Statistics calculated from the `training_data`, including minimum, maximum, and average media spend over a given period.
 *
 * Example:
 * Input:
 * const execDt = '2024-08-01';
 * const modelRuns = [
 *   { exec_dt: '2024-08-01', training_data: [...], seasonality_holidays: [...], ... },
 *   { exec_dt: '2024-07-01', training_data: [...], seasonality_holidays: [...], ... }
 * ];
 *
 * Output:
 * {
 *   seasonality: [...], // processed seasonality data
 *   rawData: {...}, // full raw data for the model run
 *   trainingInfo: {
 *     trainingDates: ['2024-07-01', '2024-07-02', ...],
 *     trainingData: [
 *       { date: '2024-07-01', SEARCH_GOOGLE: 500, DISPLAY_ADS: 300 },
 *       { date: '2024-07-02', SEARCH_GOOGLE: 520, DISPLAY_ADS: 310 }
 *     ],
 *     trainingStats: [
 *       { feature: 'SEARCH_GOOGLE', spend_30d: 15000, spend_mean: 500, spend_min: 300, spend_max: 700 },
 *       { feature: 'DISPLAY_ADS', spend_30d: 9300, spend_mean: 310, spend_min: 200, spend_max: 400 }
 *     ]
 *   }
 * }
 */
export const useModelData = (execDt, modelRuns) => {
  const { data } = useCurrentModelRunData(execDt, modelRuns);
  const { trainingData, trainingStats, trainingDates } = useModelTrainingData(data?.training_data);
  const { seasonality } = useModelSeasonality(data?.seasonality_holidays);

  return {
    seasonality,
    rawData: data,
    trainingInfo: {
      trainingDates,
      trainingData,
      trainingStats,
    },
  };
};

/**
 * useModelFeatures
 *
 * This hook processes raw data from the MMM API to extract and filter model features, then returns
 * structured information about features, channels, and a feature name map. It organizes features into
 * a human-readable format, filters them if needed, and groups them by channel and tactic.
 *
 * @param {Object} data - The raw data from the MMM API. Should include an array of `roi_mean` objects, where each object has:
 *    - `rn`: The feature name (e.g., 'SOCIAL_FACEBOOK_ADVSHOPPING_S')
 *    - `roi_mean`: The ROI mean value for that feature
 * @param {string|boolean} [filterBy=false] - Optional filter to exclude certain features (e.g., `_IMP`).
 *                                           If set to a string, features that end with this string will be excluded.
 *
 * @returns {Object} An object containing:
 *  - `modelFeatures`: Array of unique feature names from raw data where `roi_mean > 0`, sorted alphabetically
 *  - `features`: Array of processed feature objects, each containing:
 *      - `name`: Human-readable feature name (e.g., 'Social Facebook Advshopping')
 *      - `channel`: High-level channel category (e.g., 'Social', 'Display')
 *      - `tactic`: Specific tactic within channel (e.g., 'Facebook Advshopping')
 *      - `platform`: Platform type (e.g., 'social', 'display')
 *      - `value`: Original feature name (e.g., 'SOCIAL_FACEBOOK_ADVSHOPPING_S')
 *  - `channels`: Array of unique channel names (e.g., ['Social', 'Display', 'Search'])
 *
 * Example:
 * {
 *   modelFeatures: ['DISPLAY_GOOGLE_S', 'SOCIAL_FACEBOOK_ADS_S'],
 *   features: [
 *     {
 *       name: 'Display Google',
 *       channel: 'Display',
 *       tactic: 'Google',
 *       platform: 'display',
 *       value: 'DISPLAY_GOOGLE_S'
 *     },
 *     {
 *       name: 'Social Facebook Ads',
 *       channel: 'Social',
 *       tactic: 'Facebook Ads',
 *       platform: 'social',
 *       value: 'SOCIAL_FACEBOOK_ADS_S'
 *     }
 *   ],
 *   channels: ['Display', 'Social']
 * }
 * ```
 */
export const useModelFeatures = (data, filterBy = false) => {
  const { featureAliasesMap } = useContext(FeatureAliasesContext);
  const modelFeatures = useMemo(() => (
    data?.roi_mean ? _.uniq(data.roi_mean.filter(x => x.roi_mean > 0).map(x => x.rn)).sort() : false
  ), [data]);

  const [features, channels] = useMemo(() => {
    if (!modelFeatures) return [[], []];
    const rawFeatures = filterBy ? modelFeatures.filter(f => !f.endsWith(filterBy)) : modelFeatures;
    const f = buildChannelTactics(rawFeatures, featureAliasesMap);
    const c = _.uniq(f.map(g => g.channel));
    return [f, c];
  }, [modelFeatures, featureAliasesMap]);

  return {
    modelFeatures,
    features,
    channels,
  };
};

/**
 * useModelResponseCurves
 *
 * This hook processes raw data from the MMM API to compute response curves for each feature, filtering
 * the data by training spend range. It calculates the Return on Advertising Spend (ROAS) for each spend
 * input level and provides the response curves data for each feature.
 *
 * @param {Object} raw - The raw data from the MMM API, which includes:
 *    - `training_data`: An array of training data objects that provide media spend information.
 *    - `response_curves`: An array of response curve objects for each feature.
 *
 * @returns {Object} An object containing:
 *  - `loading`: A boolean indicating if the response curves are still being processed.
 *  - `responseCurves`: An object where each key is a feature name and each value is an array of response curve data for that feature, including:
 *      - `predicted_roas`: The calculated ROAS for each spend input level.
 *      - `feature_name`: The name of the feature.
 *      - `spend_input`: The amount of spend input for that feature.
 *      - `marginal_response`: The marginal response (revenue or conversion) for that spend level.
 *
 * Response Curve Calculation:
 *   - The hook processes the `response_curves` data by filtering the curves for each feature based on the minimum and maximum spend observed in the training data.
 *   - For each feature, the response curves are sorted by `spend_input` in descending order, and any duplicate `spend_input` values are removed.
 *   - The ROAS is calculated for each spend level using the formula: `predicted_roas = marginal_response / spend_input`.
 *
 * Example:
 * Input:
 * const raw = {
 *   training_data: [...], // training data array
 *   response_curves: [
 *     { feature_name: 'AMAZON_DSP_PROSPECTING_S', spend_input: 2100, marginal_response: 8808.352, sol_id: 'rb_light_mmm' },
 *     { feature_name: 'AMAZON_DSP_PROSPECTING_S', spend_input: 2000, marginal_response: 8549.331, sol_id: 'rb_light_mmm' },
 *     { feature_name: 'SEARCH_BING_S', spend_input: 1000, marginal_response: 4500, sol_id: 'rb_light_mmm' }
 *   ]
 * };
 *
 * Output:
 * {
 *   loading: false,
 *   responseCurves: {
 *     AMAZON_DSP_PROSPECTING_S: [
 *       { predicted_roas: 4.194453333333334, feature_name: 'AMAZON_DSP_PROSPECTING_S', spend_input: 2100, marginal_response: 8808.352, sol_id: 'rb_light_mmm' },
 *       { predicted_roas: 4.2746655, feature_name: 'AMAZON_DSP_PROSPECTING_S', spend_input: 2000, marginal_response: 8549.331, sol_id: 'rb_light_mmm' }
 *     ],
 *     SEARCH_BING_S: [
 *       { predicted_roas: 4.5, feature_name: 'SEARCH_BING_S', spend_input: 1000, marginal_response: 4500, sol_id: 'rb_light_mmm' }
 *     ]
 *   }
 * }
 */
export const useModelResponseCurves = raw => {
  const { features } = useModelFeatures(raw);
  const { trainingStats } = useModelTrainingData(raw?.training_data);

  const _responseCurves = useMemo(() => {
    if (!features || !raw) return;
    return features.reduce((accu, f) => {
      const featureKey = f.value;

      // filter response curves by training spend range
      const { spend_min, spend_max } = trainingStats.find(x => x.feature === featureKey) || {};
      const res = raw.response_curves
        .filter(row => row.feature_name === featureKey)
        .filter(row => row.spend_input >= spend_min && row.spend_input <= spend_max);

      // remove duplicate spend values and calc roas
      const curve = _.uniqBy(res, 'spend_input')
        .sort((a, b) => b.spend_input - a.spend_input)
        .map(row => ({ predicted_roas: row.marginal_response / row.spend_input, ...row }));

      accu[featureKey] = curve;
      return accu;
    }, {});
  }, [features, raw]);

  const responseCurves = _responseCurves || undefined;

  const loading = useMemo(() => {
    if (!_responseCurves) return true;
    return false;
  }, [_responseCurves]);

  return {
    loading,
    responseCurves,
  };
};

/**
 * useResponseCurves
 *
 * This hook processes the provided `responseCurves` object and transforms it into an array format,
 * where each entry contains a `key` (the feature name) and its associated response curve values.
 *
 * @param {Object} responseCurves - An object where each key is a feature name and each value is an array of response curve data for that feature.
 * @returns {Object} An object containing:
 *  - `responseCurvesArr`: An array of response curve objects, where each object has:
 *      - `key`: The feature name.
 *      - `values`: The response curve data for that feature.
 *
 * Example:
 * Input:
 * const responseCurves = {
 *   AMAZON_DSP_PROSPECTING_S: [{ spend_input: 2100, marginal_response: 8808.352 }],
 *   SEARCH_GOOGLE_NONBRAND_S: [{ spend_input: 1500, marginal_response: 6200 }]
 * };
 *
 * Output:
 * {
 *   responseCurvesArr: [
 *     { key: 'AMAZON_DSP_PROSPECTING_S', values: [{ spend_input: 2100, marginal_response: 8808.352 }] },
 *     { key: 'SEARCH_GOOGLE_NONBRAND_S', values: [{ spend_input: 1500, marginal_response: 6200 }] }
 *   ]
 * }
 */

export const useResponseCurves = ({ responseCurves }) => {
  const responseCurvesArr = useMemo(() => {
    if (!responseCurves) return;
    return Object.entries(responseCurves).map(([k, v]) => ({
      key: k,
      values: v,
    }));
  }, [responseCurves]);

  return { responseCurvesArr };
};
